import React from 'react';
import { observer } from 'mobx-react-lite';
import { Card } from 'primereact/card';
import { ProgressSpinner } from 'primereact/progressspinner';
import type { ApplicationSdk } from '../core/sdk';

interface IframeViewProps {
    sdk: ApplicationSdk;
    menuKey: string;
}

export const IframeView = observer(({ sdk, menuKey }: IframeViewProps) => {
    const [isLoading, setIsLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);

    // Find the menu item by key
    const menuItem = sdk.menuData.find((item: any) => item.key === menuKey);

    if (!menuItem) {
        return (
            <Card
                title="Fehler"
                style={{ margin: '1rem', textAlign: 'center' }}
            >
                <p>Das angeforderte Menü-Element "{menuKey}" wurde nicht gefunden.</p>
            </Card>
        );
    }

    if (menuItem.type !== 'iframe') {
        return (
            <Card
                title="Fehler"
                style={{ margin: '1rem', textAlign: 'center' }}
            >
                <p>Das Menü-Element "{menuItem.label}" ist nicht als iframe konfiguriert.</p>
            </Card>
        );
    }

    const handleIframeLoad = () => {
        setIsLoading(false);
        setError(null);
    };

    const handleIframeError = () => {
        setIsLoading(false);
        setError('Fehler beim Laden des Inhalts');
    };

    let iframeConfig: any = {};
    if (menuItem.config) {
        try {
            iframeConfig = JSON.parse(menuItem.config);
        } catch (e) {
            console.warn('Invalid iframe config JSON:', menuItem.config);
        }
    }

    const iframeStyles = {
        width: '100%',
        height: iframeConfig.height || '800px',
        border: 'none',
        borderRadius: '8px',
        ...iframeConfig.style,
    };

    return (
        <div style={{ height: '100%', overflow: 'hidden' }}>
            <Card
                style={{
                    boxShadow: '0 2px 3px rgba(0, 0, 0, 0.1)',
                    borderRadius: '8px',
                    padding: '0',
                }}
                pt={{
                    body: { style: { padding: '0' } },
                    content: { style: { padding: '0' } },
                }}
            >
                {isLoading && (
                    <div
                        className="flex justify-content-center align-items-center flex-column"
                        style={{ height: '200px' }}
                    >
                        <ProgressSpinner />
                        <span style={{ marginTop: '1rem' }}>Lade Inhalt...</span>
                    </div>
                )}
                {error && (
                    <div
                        className="flex justify-content-center align-items-center"
                        style={{ height: '200px', color: 'red' }}
                    >
                        <i
                            className="pi pi-exclamation-triangle"
                            style={{ marginRight: '0.5rem' }}
                        ></i>
                        <span>{error}</span>
                    </div>
                )}

                <iframe
                    src={menuItem.path}
                    title={menuItem.label}
                    style={{
                        ...iframeStyles,
                        display: isLoading || error ? 'none' : 'block',
                    }}
                    onLoad={handleIframeLoad}
                    onError={handleIframeError}
                    allow={
                        iframeConfig.allow ||
                        'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
                    }
                    sandbox={
                        iframeConfig.sandbox ||
                        'allow-same-origin allow-scripts allow-popups allow-forms'
                    }
                />
            </Card>
        </div>
    );
});

export default IframeView;
